import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Button } from '@src/common/components/button/Button';
import { Checkbox } from '@src/common/components/Checkbox';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { Tooltip } from '@src/common/components/Tooltip';
import { EventEmitter } from '@src/common/EventEmitter';
import { useToast } from '@src/common/hooks/useToast';
import { ClientInformationSection } from '@src/orders/ClientInformationSection';
import { DuplicateOrderFooter } from '@src/orders/components/DuplicateOrderFooter';
import { PermitStatusBadge } from '@src/orders/components/PermitStatusBadge';
import type { Order } from '@src/orders/domain/Order';
import { transformToPermitStatus } from '@src/orders/domain/PermitApplication';
import { duplicateOrderEvents } from '@src/orders/DuplicateOrderEvents';
import { useDuplicateOrder } from '@src/orders/hooks/useDuplicateOrder';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import type {
  PermitSelectionColumns,
  PermitSelectionRowsData,
} from '@src/orders/PermitSelectionSection';
import { PermitSelectionSection } from '@src/orders/PermitSelectionSection';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  order: Order;
}

export const DuplicateOrder = ({ order }: Props) => {
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const navigate = useNavigate();

  const activePermitsIds = order.permitApplicationGroups
    .filter((permit) => permit.originalPermit.permitTypeIsActive)
    .map((permit) => permit.originalPermit.application.id);
  const [selectedPermitIds, setSelectedPermitIds] =
    useState<string[]>(activePermitsIds);
  const [isSectionCollapsed, setIsSectionCollapsed] = useState(false);
  const { user: currentUser } = useAuthentication();

  const columns: PermitSelectionColumns[] = [
    {
      field: 'select',
      headerName: '',
      width: 50,
      render: (row: PermitSelectionRowsData) => {
        const permitId = row.id;
        const isChecked = selectedPermitIds.includes(permitId);
        const isActive = row.permitTypeIsActive ?? true;
        const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
          if (event.target.checked) {
            setSelectedPermitIds((prev) => [...prev, permitId]);
          } else {
            setSelectedPermitIds((prev) =>
              prev.filter((id) => id !== permitId),
            );
          }
        };
        const checkbox = (
          <Checkbox
            checked={isChecked && isActive}
            disabled={!isActive}
            label=""
            onChange={!isActive ? () => {} : handleCheckboxChange}
            data-testid={`permit-checkbox-${permitId}`}
          />
        );

        return !isActive ? (
          <Tooltip title={t('duplicateOrderInactivePermitTypeTooltip')}>
            <div>{checkbox}</div>
          </Tooltip>
        ) : (
          checkbox
        );
      },
    },
    { field: 'name', headerName: t('permitType.name') },
    { field: 'type', headerName: t('permitType.type') },
    { field: 'jurisdiction', headerName: t('permitType.jurisdiction') },
    {
      field: 'status',
      headerName: t('permitSummaries.status'),
      render: (row) => getPermitStatusBadge(row.status),
    },
  ];

  const rows: PermitSelectionRowsData[] = order.permitApplicationGroups.map(
    (permit) => {
      return {
        id: permit.originalPermit.application.id,
        name: permit.originalPermit.permitTypeName,
        type: permit.originalPermit.permitType,
        jurisdiction: permit.originalPermit.permitTypeJurisdiction,
        status: permit.originalPermit.application.status,
        permitTypeIsActive: permit.originalPermit.permitTypeIsActive,
      };
    },
  );

  const { navigateToOrderDetails } = useNavigateToOrderDetails();

  const hasSelectedPermits = selectedPermitIds.length > 0;

  const { duplicateOrder } = useDuplicateOrder({
    orderId: order.id,
    data: {
      permitsToCopy: selectedPermitIds,
    },
    onSuccess: (duplicatedOrderId: string) => {
      navigate(OrdersNavigation.edit.url(duplicatedOrderId));
    },
    onError: () => {
      showErrorToast(t('duplicateOrderError'));
    },
  });

  const handleContinueDuplication = () => {
    duplicateOrder();
  };

  const handleCancelDuplication = async () => {
    await navigateToOrderDetails({ orderId: order.id });
  };

  const toggleCollapse = () => {
    if (isSectionCollapsed) {
      EventEmitter.emit(duplicateOrderEvents.uncollapseEventName);
    } else {
      EventEmitter.emit(duplicateOrderEvents.collapseEventName);
    }
    setIsSectionCollapsed((previous) => !previous);
  };

  return (
    <PageLayout
      title={t('duplicateOrder') + ' #' + order.displayId}
      subtitle={t('duplicateOrderInstructions')}
      {...duplicateOrderEvents}
    >
      <>
        <Stack direction="row" justifyContent="end">
          <Button onClick={toggleCollapse} aria-label="toggle-collapse">
            {isSectionCollapsed ? t('uncollapseAll') : t('collapseAll')}
          </Button>
        </Stack>
        <Stack direction="column" gap={4}>
          <ClientInformationSection
            defaultClientId={order.clientId}
            readonly={true}
            isClientUser={isClientUser(currentUser)}
          />
          <PermitSelectionSection
            columns={columns}
            rows={rows}
            {...duplicateOrderEvents}
          ></PermitSelectionSection>
          <DuplicateOrderFooter
            onClose={handleCancelDuplication}
            onContinue={handleContinueDuplication}
            disabled={!hasSelectedPermits}
          />
        </Stack>
      </>
    </PageLayout>
  );
};

const getPermitStatusBadge = (stringStatus: string) => {
  const status = transformToPermitStatus(stringStatus);
  return <PermitStatusBadge status={status} />;
};
